import {
  Box,
  ButtonBase,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { PTIcon, PTTypography } from "../../components/ui";
import { getPrivateRoutes } from "../../router/privateRoutes";
import { APP_ACTIONS, NAVCODE, USER_ROLES } from "../../types";
import LabelRoute from "./LabelRoute";
import { useAuth, useDialog } from "../../hooks";
import JWtUtils from "../../utils/JWtUtils";
import { useProfileImage } from "../../hooks/useProfileImage";

export const SecondHeader = ({ user, isMobile, handleClick, ...props }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        height: "55px",
        mt: "20px",
        pl: "20px",
        backgroundColor: "primary.main",
        borderRadius: "20px",
        ...(isMobile && { height: "40px", pl: "1px", minWidth: "96vw" }),
        ...(!isMobile && { width: "100%" }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          alignItems: "center",
          ...(isMobile && { gap: "1px", maxWidth: "40vw" }),
        }}
      >
        {getPrivateRoutes()
          .filter((r) => !r.isHidden?.(user?.statusCode))
          .filter((r) => r.nav.includes(NAVCODE.SECOND_TOP))
          .map((r) => {
            return (
              <LabelRoute
                key={r.key}
                {...r}
                isMobile={isMobile}
                handleClick={handleClick}
              />
            );
          })}
      </Box>
      <Box
        sx={{
          mr: "20px",
          justifyContent: "flex-end",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          ...(isMobile && { mr: "1px" }),
        }}
      >
        <ProfileBox user={user} isMobile={isMobile} />
        <MoreOptionsIcon isMobile={isMobile} />
      </Box>
    </Box>
  );
};

const ProfileBox = ({ user, isMobile }) => {
  const roles = localStorage.getItem("roles");
  const { profileImageUrl, loadingImage, fetchProfileImage } = useProfileImage();

  const hasCompanyRole = JWtUtils.hasAnyRole([
    USER_ROLES.MANUFACTURER,
    USER_ROLES.WORKER,
  ]);

  const getDisplayText = (user) => {
    if (!user) return "User";

    if (hasCompanyRole && user.company) {
      return user.company;
    } else {
      return roles;
    }
  };

  useEffect(() => {
    if (hasCompanyRole && user?.manufacturer?.profileImageGuid) {
      fetchProfileImage(
        user.manufacturer.profileImageGuid,
        user.manufacturer.profileImageFileName
      );
    }
  }, [user?.manufacturer?.profileImageGuid, hasCompanyRole]);

  return (
    <Box
      sx={{
        width: isMobile ? 120 : 160.25,
        height: isMobile ? 26 : 35,
        mr: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "8px",
        overflow: "hidden", 
      }}
    >
      <Box
        sx={{
          width: hasCompanyRole ? (isMobile ? 85 : 113.65) : "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#232A56",
        }}
      >
        <PTTypography
          sx={{
            fontSize: isMobile ? "12px" : "14px",
            color: "#FFFFFF",
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getDisplayText(user)}
        </PTTypography>
      </Box>

      {hasCompanyRole && (
        <Box
          sx={{
            width: isMobile ? 35 : 46.6,
            height: "100%",
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {loadingImage ? (
            <CircularProgress size={isMobile ? 16 : 20} />
          ) : profileImageUrl ? (
            <Box
              component="img"
              src={profileImageUrl}
              alt="Profile"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          ) : (
            <PTIcon
              icon="person"
              sx={{ color: "#9e9e9e", fontSize: isMobile ? 16 : 24 }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const MoreOptionsIcon = ({ isMobile, ...props }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { logout } = useAuth();
  const { showDialog } = useDialog();

  const handelLogOut = () => {
    showDialog(
      {
        title: "logout_action",
        description: "logout_description",
      },
      () => {
        logout();
      },
      () => {
        // do nothing
      }
    );
  };

  // Open dropdown
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        sx={{ ml: 1, display: "flex", alignItems: "center" }}
        onClick={handleOpen}
      >
        <PTIcon
          icon={anchorEl ? "keyboardArrowUp" : "keyboardArrowDown"}
          sx={{
            height: "30px",
            width: "fit-content",
            backgroundColor: "#EBEBEB",
            borderRadius: "50%",
            "&:hover": {
              backgroundColor: "#25384a",
              color: "#FFFFFF",
            },
            ...(isMobile && { height: "20px" }),
          }}
        />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {getPrivateRoutes()
          .filter((r) => r.nav.includes(NAVCODE.EXTRA))
          .map((child, index) => (
            <MenuItem
              key={index}
              disabled={child.disabled}
              onClick={() => {
                if (child.disabled) return;
                if (child.action === APP_ACTIONS.LOGOUT) {
                  handelLogOut();
                }
                if (child.path) {
                  //navigate(child.path);
                }
                handleClose();
              }}
            >
              <PTTypography
                sx={{ fontFamily: "Sora", fontSize: "16px", color: "#000000" }}
              >
                {child.label}
              </PTTypography>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default SecondHeader;
